import { withProps } from "@udecode/cn";
import { createAlignPlugin } from "@udecode/plate-alignment";
import { createAutoformatPlugin } from "@udecode/plate-autoformat";
import {
	createBoldPlugin,
	createCodePlugin,
	createItalicPlugin,
	createStrikethroughPlugin,
	createSubscriptPlugin,
	createSuperscriptPlugin,
	createUnderlinePlugin,
	MARK_BOLD,
	MARK_CODE,
	MARK_ITALIC,
	MARK_STRIKETHROUGH,
	MARK_SUBSCRIPT,
	MARK_SUPERSCRIPT,
	MARK_UNDERLINE,
} from "@udecode/plate-basic-marks";
import {
	createBlockquotePlugin,
	ELEMENT_BLOCKQUOTE,
} from "@udecode/plate-block-quote";
import {
	createExitBreakPlugin,
	createSoftBreakPlugin,
} from "@udecode/plate-break";
import { createCaptionPlugin } from "@udecode/plate-caption";
import {
	createCodeBlockPlugin,
	ELEMENT_CODE_BLOCK,
	ELEMENT_CODE_LINE,
	ELEMENT_CODE_SYNTAX,
	isCodeBlockEmpty,
	isSelectionAtCodeBlockStart,
	unwrapCodeBlock,
} from "@udecode/plate-code-block";
import { createComboboxPlugin } from "@udecode/plate-combobox";
import {
	createPlugins,
	isBlockAboveEmpty,
	isSelectionAtBlockStart,
	PlateElement,
	PlateLeaf,
} from "@udecode/plate-common";
import type { RenderAfterEditable } from "@udecode/plate-common";
import { createEmojiPlugin } from "@udecode/plate-emoji";
import {
	createFontBackgroundColorPlugin,
	createFontColorPlugin,
	createFontSizePlugin,
} from "@udecode/plate-font";
import {
	createHeadingPlugin,
	ELEMENT_H1,
	ELEMENT_H2,
	ELEMENT_H3,
	ELEMENT_H4,
	ELEMENT_H5,
	ELEMENT_H6,
	KEYS_HEADING,
} from "@udecode/plate-heading";
import {
	createHighlightPlugin,
	MARK_HIGHLIGHT,
} from "@udecode/plate-highlight";
import {
	createHorizontalRulePlugin,
	ELEMENT_HR,
} from "@udecode/plate-horizontal-rule";
import { createIndentPlugin } from "@udecode/plate-indent";
import { createIndentListPlugin } from "@udecode/plate-indent-list";
import { createJuicePlugin } from "@udecode/plate-juice";
import { createKbdPlugin, MARK_KBD } from "@udecode/plate-kbd";
import { createLinkPlugin, ELEMENT_LINK } from "@udecode/plate-link";
import {
	ELEMENT_LI,
	ELEMENT_OL,
	ELEMENT_TODO_LI,
	ELEMENT_UL,
} from "@udecode/plate-list";
import {
	createImagePlugin,
	createMediaEmbedPlugin,
	ELEMENT_IMAGE,
	ELEMENT_MEDIA_EMBED,
} from "@udecode/plate-media";
import { createNodeIdPlugin } from "@udecode/plate-node-id";
import {
	createParagraphPlugin,
	ELEMENT_PARAGRAPH,
} from "@udecode/plate-paragraph";
import { createResetNodePlugin } from "@udecode/plate-reset-node";
import { createSelectOnBackspacePlugin } from "@udecode/plate-select";
import { createBlockSelectionPlugin } from "@udecode/plate-selection";
import {
	createTablePlugin,
	ELEMENT_TABLE,
	ELEMENT_TD,
	ELEMENT_TH,
	ELEMENT_TR,
} from "@udecode/plate-table";
import { createTrailingBlockPlugin } from "@udecode/plate-trailing-block";
import { ELEMENT_TOGGLE, createTogglePlugin } from "@udecode/plate-toggle";

import { autoformatPlugin } from "@/components/cms/ContentEditor/lib/autoformatPlugin";
import { BlockquoteElement } from "@/components/cms/ContentEditor/components/BlockquoteElement";
import { CodeBlockElement } from "@/components/cms/ContentEditor/components/CodeBlockElement";
import { CodeLeaf } from "@/components/cms/ContentEditor/components/CodeLeaf";
import { CodeLineElement } from "@/components/cms/ContentEditor/components/CodeLineElement";
import { CodeSyntaxLeaf } from "@/components/cms/ContentEditor/components/CodeSyntaxLeaf";
import { EmojiCombobox } from "@/components/cms/ContentEditor/components/EmojiCombobox";
import { HeadingElement } from "@/components/cms/ContentEditor/components/HeadingElement";
import { HighlightLeaf } from "@/components/cms/ContentEditor/components/HighlightLeaf";
import { HrElement } from "@/components/cms/ContentEditor/components/HrElement";
import { ImageElement } from "@/components/cms/ContentEditor/components/ImageElement";
import { KbdLeaf } from "@/components/cms/ContentEditor/components/KbdLeaf";
import { LinkElement } from "@/components/cms/ContentEditor/components/LinkElement";
import { LinkFloatingToolbar } from "@/components/cms/ContentEditor/components/LinkFloatingToolbar";
import { ListElement } from "@/components/cms/ContentEditor/components/ListElement";
import { MediaEmbedElement } from "@/components/cms/ContentEditor/components/MediaEmbedElement";
import { ParagraphElement } from "@/components/cms/ContentEditor/components/ParagraphElement";
import { withPlaceholders } from "@/components/cms/ContentEditor/components/Placeholder";
import { ToggleElement } from "@/components/cms/ContentEditor/components/ToggleElement";
import {
	TableCellElement,
	TableCellHeaderElement,
} from "@/components/cms/ContentEditor/components/TableCellElement";
import { TableElement } from "@/components/cms/ContentEditor/components/TableElement";
import { TableRowElement } from "@/components/cms/ContentEditor/components/TableRowElement";
import { ColumnElement } from "@/components/cms/ContentEditor/components/ColumnElement";
import { ColumnGroupElement } from "@/components/cms/ContentEditor/components/ColumnGroupElement";
import * as plateLayout from "@udecode/plate-layout";

const resetBlockTypesCommonRule = {
	types: [ELEMENT_BLOCKQUOTE, ELEMENT_TODO_LI],
	defaultType: ELEMENT_PARAGRAPH,
};

const resetBlockTypesCodeBlockRule = {
	types: [ELEMENT_CODE_BLOCK],
	defaultType: ELEMENT_PARAGRAPH,
	onReset: unwrapCodeBlock,
};

export const plugins = createPlugins(
	[
		// Nodes
		createParagraphPlugin(),
		createHeadingPlugin(),
		createBlockquotePlugin(),
		createCodeBlockPlugin(),
		createHorizontalRulePlugin(),
		// FYI: possibility to validate URL on our end with options.isUrl(),
		// example if we want relative URLs
		createLinkPlugin({
			renderAfterEditable: LinkFloatingToolbar as RenderAfterEditable,
		}),
		createImagePlugin(),
		createMediaEmbedPlugin(),
		createCaptionPlugin({
			options: { pluginKeys: [ELEMENT_IMAGE, ELEMENT_MEDIA_EMBED] },
		}),
		createTablePlugin(),

		// Marks
		createBoldPlugin(),
		createItalicPlugin(),
		createUnderlinePlugin(),
		createStrikethroughPlugin(),
		createCodePlugin(),
		createSubscriptPlugin(),
		createSuperscriptPlugin(),
		createFontColorPlugin(),
		createFontBackgroundColorPlugin(),
		createFontSizePlugin(),
		createHighlightPlugin(),
		createKbdPlugin(),

		// Block Style
		createAlignPlugin({
			inject: {
				props: {
					validTypes: [ELEMENT_PARAGRAPH, ELEMENT_H1, ELEMENT_H2, ELEMENT_H3],
				},
			},
		}),
		createIndentPlugin({
			inject: {
				props: {
					validTypes: [
						ELEMENT_PARAGRAPH,
						ELEMENT_H1,
						ELEMENT_H2,
						ELEMENT_H3,
						ELEMENT_BLOCKQUOTE,
						ELEMENT_CODE_BLOCK,
						ELEMENT_TOGGLE,
					],
				},
			},
		}),
		createIndentListPlugin({
			inject: {
				props: {
					validTypes: [
						ELEMENT_PARAGRAPH,
						ELEMENT_H1,
						ELEMENT_H2,
						ELEMENT_H3,
						ELEMENT_BLOCKQUOTE,
						ELEMENT_CODE_BLOCK,
						ELEMENT_TOGGLE,
					],
				},
			},
		}),

		// Functionality
		createAutoformatPlugin(autoformatPlugin),
		createBlockSelectionPlugin({
			options: {
				sizes: {
					top: 0,
					bottom: 0,
				},
			},
		}),
		createComboboxPlugin(),
		createEmojiPlugin({
			renderAfterEditable: EmojiCombobox as RenderAfterEditable,
		}),
		createExitBreakPlugin({
			options: {
				rules: [
					{
						hotkey: "mod+enter",
					},
					{
						hotkey: "mod+shift+enter",
						before: true,
					},
					{
						hotkey: "enter",
						query: {
							start: true,
							end: true,
							allow: KEYS_HEADING,
						},
						relative: true,
						level: 1,
					},
				],
			},
		}),
		createNodeIdPlugin(),
		createResetNodePlugin({
			options: {
				rules: [
					{
						...resetBlockTypesCommonRule,
						hotkey: "Enter",
						predicate: isBlockAboveEmpty,
					},
					{
						...resetBlockTypesCommonRule,
						hotkey: "Backspace",
						predicate: isSelectionAtBlockStart,
					},
					{
						...resetBlockTypesCodeBlockRule,
						hotkey: "Enter",
						predicate: isCodeBlockEmpty,
					},
					{
						...resetBlockTypesCodeBlockRule,
						hotkey: "Backspace",
						predicate: isSelectionAtCodeBlockStart,
					},
				],
			},
		}),
		createSelectOnBackspacePlugin({
			options: {
				query: {
					allow: [ELEMENT_IMAGE, ELEMENT_HR],
				},
			},
		}),

		createSoftBreakPlugin({
			options: {
				rules: [
					{ hotkey: "shift+enter" },
					{
						hotkey: "enter",
						query: {
							allow: [ELEMENT_CODE_BLOCK, ELEMENT_BLOCKQUOTE, ELEMENT_TD],
						},
					},
				],
			},
		}),
		createTrailingBlockPlugin({
			options: { type: ELEMENT_PARAGRAPH },
		}),

		// Deserialization
		createJuicePlugin(),
		plateLayout.createColumnPlugin(),
		createTogglePlugin(),
	] as any,
	{
		components: withPlaceholders({
			[ELEMENT_BLOCKQUOTE]: BlockquoteElement,
			[ELEMENT_CODE_BLOCK]: CodeBlockElement,
			[ELEMENT_CODE_LINE]: CodeLineElement,
			[ELEMENT_CODE_SYNTAX]: CodeSyntaxLeaf,
			[plateLayout.ELEMENT_COLUMN]: ColumnElement,
			[plateLayout.ELEMENT_COLUMN_GROUP]: ColumnGroupElement,
			[ELEMENT_HR]: HrElement,
			[ELEMENT_H1]: withProps(HeadingElement, { variant: "h1" }),
			[ELEMENT_H2]: withProps(HeadingElement, { variant: "h2" }),
			[ELEMENT_H3]: withProps(HeadingElement, { variant: "h3" }),
			[ELEMENT_H4]: withProps(HeadingElement, { variant: "h4" }),
			[ELEMENT_H5]: withProps(HeadingElement, { variant: "h5" }),
			[ELEMENT_H6]: withProps(HeadingElement, { variant: "h6" }),
			[ELEMENT_IMAGE]: ImageElement,
			[ELEMENT_LI]: withProps(PlateElement, { as: "li" }),
			[ELEMENT_LINK]: LinkElement,
			[ELEMENT_MEDIA_EMBED]: MediaEmbedElement,
			[ELEMENT_UL]: withProps(ListElement, { variant: "ul" }),
			[ELEMENT_OL]: withProps(ListElement, { variant: "ol" }),
			[ELEMENT_PARAGRAPH]: ParagraphElement,
			[ELEMENT_TABLE]: TableElement,
			[ELEMENT_TD]: TableCellElement,
			[ELEMENT_TH]: TableCellHeaderElement,
			[ELEMENT_TOGGLE]: ToggleElement,
			[ELEMENT_TR]: TableRowElement,
			[MARK_BOLD]: withProps(PlateLeaf, { as: "strong" }),
			[MARK_CODE]: CodeLeaf,
			[MARK_HIGHLIGHT]: HighlightLeaf,
			[MARK_ITALIC]: withProps(PlateLeaf, { as: "em" }),
			[MARK_KBD]: KbdLeaf,
			[MARK_STRIKETHROUGH]: withProps(PlateLeaf, { as: "s" }),
			[MARK_SUBSCRIPT]: withProps(PlateLeaf, { as: "sub" }),
			[MARK_SUPERSCRIPT]: withProps(PlateLeaf, { as: "sup" }),
			[MARK_UNDERLINE]: withProps(PlateLeaf, { as: "u" }),
		}),
	}
);
